// The full extraction of one NIK, shown in three places with one layout:
// the Cek NIK readout, and the detail dialog opened from a Kiriman row or a
// Data relawan row. Groups: identity (who sent it), kode wilayah, kelahiran,
// informasi turunan, kelayakan hukum, validasi. Label/value pairs, no chart.
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useDashboardApi } from "./api";
import { useResource } from "./data";
import { formatDateTime } from "./format";
import type { NikCheck, SubmissionDetail } from "./types";
import { LoadError, Notice, RowsSkeleton, StatusBadge } from "./ui";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export const STATUS_LABEL: Record<NikCheck["status"], { tone: "success" | "info" | "danger" | "neutral"; text: string }> = {
  valid: { tone: "success", text: "Valid" },
  review: { tone: "info", text: "Perlu ditinjau" },
  rejected: { tone: "danger", text: "Ditolak JEV" },
  invalid: { tone: "danger", text: "Tidak valid" },
};

function Group({ title, rows }: { title: string; rows: [string, ReactNode][] }) {
  return (
    <section className="ex-group">
      <h3 className="ex-group-title">{title}</h3>
      <dl className="dm-pairs">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const yes = (b: boolean) => (b ? "Ya" : "Belum");

export function EkstraksiView({ check, nik, identity }: { check: NikCheck; nik?: string; identity?: [string, ReactNode][] }) {
  const d = check.decoded;
  const t = check.turunan;
  const status = STATUS_LABEL[check.status];
  return (
    <div className="ex-view">
      <div className="dm-result-head">
        <p className="kit-title">{d ? `${d.jenisKelamin}, ${d.kecamatan ?? "kecamatan tidak dikenal"}` : "NIK tidak dapat dibaca"}</p>
        <StatusBadge tone={status.tone}>{status.text}</StatusBadge>
      </div>
      {check.hard.length || check.soft.length ? (
        <ul className="dm-flags">
          {[...check.hard, ...check.soft].map((f) => (
            <li key={f.code}>{f.reason}</li>
          ))}
        </ul>
      ) : null}
      <div className="ex-groups">
        {identity ? <Group title="Kiriman" rows={identity} /> : null}
        {d ? (
          <Group
            title="Kode wilayah"
            rows={[
              ...(nik ? ([["NIK", <span className="kit-num">{nik}</span>]] as [string, ReactNode][]) : []),
              ["Provinsi", `${d.provinsi ?? "Tidak terdaftar"} (${d.kode.provinsi})`],
              ["Kabupaten/kota", `${d.kabkota ?? "Tidak terdaftar"} (${d.kode.kabkota})`],
              ["Kecamatan", `${d.kecamatan ?? "Tidak terdaftar"} (${d.kode.kecamatan})`],
              ["Nomor urut", <span className="kit-num">{d.kode.serial}</span>],
            ]}
          />
        ) : null}
        {d ? (
          <Group
            title="Kelahiran"
            rows={[
              ["Jenis kelamin", d.jenisKelamin],
              [
                "Tanggal lahir",
                `${d.tanggalLahir.hari} ${BULAN[d.tanggalLahir.bulan - 1] ?? `bulan ${d.tanggalLahir.bulan}`} ${d.tanggalLahir.tahun}${d.tanggalLahir.valid ? "" : " (tidak valid)"}`,
              ],
              ...(t ? ([["Hari lahir", t.hariLahir]] as [string, ReactNode][]) : []),
              ["Kode tanggal", <span className="kit-num">{d.kode.tanggal}</span>],
            ]}
          />
        ) : null}
        {t ? (
          <Group
            title="Informasi turunan"
            rows={[
              ["Usia", `${t.umur} tahun`],
              ["Kelompok usia", t.kelompokUsia[0]!.toUpperCase() + t.kelompokUsia.slice(1)],
              ["Generasi", t.generasi],
              ["Zodiak", t.zodiak],
              ["Shio", `${t.shio}${t.shioPasti ? "" : " (perkiraan, lahir sekitar Imlek)"}`],
              ["Zona waktu", t.zonaWaktu ?? "Tidak dikenal"],
            ]}
          />
        ) : null}
        {t ? (
          <Group
            title="Kelayakan hukum"
            rows={[
              ["Hak pilih", t.hukum.hakPilih ? (t.hukum.pemilihPemula ? "Ya, pemilih pemula" : "Ya") : "Belum (di bawah 17)"],
              ["Wajib KTP-el", yes(t.hukum.wajibKtp)],
              ...t.hukum.sim.map((s): [string, ReactNode] => [`SIM ${s.golongan}`, s.layak ? "Cukup umur" : `Mulai ${s.layakTahun}`]),
              ["Pensiun JP BPJS", `${t.hukum.pensiunJp.tahun} (usia ${t.hukum.pensiunJp.usia})${t.hukum.pensiunJp.sisaTahun ? `, ${t.hukum.pensiunJp.sisaTahun} tahun lagi` : ""}`],
              ["Pensiun ASN", `BUP 58: ${t.hukum.pensiunAsn.bup58}, BUP 60: ${t.hukum.pensiunAsn.bup60}`],
            ]}
          />
        ) : null}
        <Group
          title="Validasi"
          rows={[
            ["Status", status.text],
            ["Aturan keras", check.hard.length ? `${check.hard.length} gagal` : "Lolos"],
            ["Tanda lunak", check.soft.length ? `${check.soft.length}` : "Tidak ada"],
            [
              "JEV",
              check.jev
                ? check.jev.verified
                  ? `${check.jev.choice}, keyakinan ${Math.round((check.jev.confidence ?? 0) * 100)}%`
                  : `Tidak tersedia (${check.jev.stamp ?? "UNVERIFIED BY JEV"})`
                : check.soft.length
                  ? "Belum dinilai"
                  : "Tidak perlu",
            ],
          ]}
        />
      </div>
    </div>
  );
}

/** Detail dialog for one submission: loads GET /dashboard/demografi/kiriman/:id. */
export function EkstraksiDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || typeof dialog.showModal !== "function") return;
    if (id && !dialog.open) dialog.showModal();
    if (!id && dialog.open) dialog.close();
  }, [id]);

  return (
    <dialog
      ref={ref}
      className="dash-dialog ex-dialog"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {id ? <DetailBody id={id} titleId={titleId} onClose={onClose} /> : null}
    </dialog>
  );
}

function DetailBody({ id, titleId, onClose }: { id: string; titleId: string; onClose: () => void }) {
  const api = useDashboardApi();
  const res = useResource<SubmissionDetail>(() => api.submissionDetail(id), [api, id]);
  const k = res.data?.kiriman;
  return (
    <div className="dash-dialog-inner">
      <div className="ex-dialog-head">
        <h2 id={titleId} className="kit-title">
          {k ? k.nama : "Ekstraksi NIK"}
        </h2>
        <button type="button" className="btn-ghost" onClick={onClose} autoFocus>
          Tutup
        </button>
      </div>
      {res.error && !res.data ? (
        <LoadError what="Ekstraksi" onRetry={res.reload} />
      ) : !res.data || !k ? (
        <RowsSkeleton rows={6} />
      ) : (
        <>
          {!res.data.terprofil ? (
            <Notice tone="info" title="Profil sedang dibuat">
              Kiriman ini baru masuk; validasi JEV menyusul dalam beberapa detik.
            </Notice>
          ) : null}
          <EkstraksiView
            check={res.data.ekstraksi}
            nik={k.nik}
            identity={[
              ["Nama", k.nama],
              ["Nomor", <span className="kit-num">{k.nomor}</span>],
              ["Pengirim", k.pengirim ?? "Tanpa label"],
              ["Diterima", formatDateTime(k.diterima)],
            ]}
          />
        </>
      )}
    </div>
  );
}
